import { call, put, select, takeEvery } from 'redux-saga/effects';
import { logDmbErrorLocation } from '../utils/error';
import { reqAgVizMaps } from '../actions/agVizActions';
import { DMB_WEB_AG_VIZ } from '../constants/featureFlags';
import { SET_ACTIVE_SELECTOR_VALS, RECEIVE_SELECTOR_VALS } from '../constants/actionTypes';
import { getDmbLayerIdsForActivity } from '../utils/agVizLayerMapper';
import { SeasonName as AgVizSeasonName, AgronomicActivityType } from '../requesters/agVizRequester/types';
import { buildSeasonContext } from '../utils/seasonUtils';

/**
 * Request Ag-Viz maps for the active season
 * 
 * @param {Object} action - SET_ACTIVE_SELECTOR_VALS or RECEIVE_SELECTOR_VALS action
 * @return {void}
 */
export function* requestAgVizMapsForActiveSeason(action) {
  try {
    const state = yield select();
    const agVizEnabled = state.feature?.userConfigFeatures?.[DMB_WEB_AG_VIZ]?.value;

    if (!agVizEnabled) {
      return;
    }

    const activeSeasons = action.activeSeasons || action.activeVals?.activeSeasons;
    const fieldUuid = state.field?.uuid;
    
    if (!activeSeasons || activeSeasons.length === 0 || !fieldUuid) {
      return;
    }

    const activeSeasonId = activeSeasons[0].id;
    
    // Check if we already have Ag-Viz data for this season
    const seasonLayers = state.layers?.list?.[activeSeasonId];
    const hasExistingAgVizData = seasonLayers && Object.values(seasonLayers).some(
      (layer) => typeof layer === 'object' && layer !== null && 'agVizTileUrl' in layer && layer.agVizTileUrl
    );
    
    if (hasExistingAgVizData) {
      return;
    }
    
    // Get all supported Ag-Viz layer IDs
    const supportedPlantingLayers = getDmbLayerIdsForActivity(AgronomicActivityType.PLANTING);
    const supportedHarvestLayers = getDmbLayerIdsForActivity(AgronomicActivityType.HARVESTING);
    
    // Parse season tag to extract year, crop code, season name using centralized utilities
    const seasonParts = activeSeasonId.split(':');
    if (seasonParts.length < 5) {
      return;
    }

    const [, cropCode] = seasonParts;
    const seasonContext = buildSeasonContext(activeSeasonId, undefined);
    const seasonName = seasonContext.seasonName as AgVizSeasonName;
    const seasonYear = seasonContext.year;

    // Request planting data if we have supported planting layers
    if (supportedPlantingLayers.length > 0) {
      yield put(
        reqAgVizMaps({
          fieldId: fieldUuid,
          cropCode,
          seasonName,
          seasonYear,
          activityType: AgronomicActivityType.PLANTING,
        })
      );
    }

    // Request harvest data if we have supported harvest layers
    if (supportedHarvestLayers.length > 0) {
      yield put(
        reqAgVizMaps({
          fieldId: fieldUuid,
          cropCode,
          seasonName,
          seasonYear,
          activityType: AgronomicActivityType.HARVESTING,
        })
      );
    }
  } catch (error) {
    logDmbErrorLocation(error, 'agVizInitSaga - requestAgVizMapsForActiveSeason');
  }
}

/**
 * Watcher saga for season changes and initial load
 * @return {void}
 */
export function* agVizInitWatcher() {
  yield takeEvery([SET_ACTIVE_SELECTOR_VALS, RECEIVE_SELECTOR_VALS], requestAgVizMapsForActiveSeason);
}

export default function* agVizInitSaga() {
  yield call(agVizInitWatcher);
}
